import { useFormContext, Controller, useWatch } from "react-hook-form";
import {
    Radio,
    RadioGroup,
    FormHelperText,
    RadioGroupProps,
    FormControlLabel,
    styled,
    Paper,
    Box,
} from "@mui/material";
import { ReactNode } from "react";

const OptionStyle = styled(Paper, {
    shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ theme, selected }) => ({
    flex: 1,
    width: "100%",
    padding: theme.spacing(3),
    backgroundColor: "#F9F4F4",
    border: selected ? `1.5px dashed ${theme.palette.primary.main}` : "1.5px solid transparent",
    boxShadow: selected ? "0 8px 16px 0 rgba(255, 102, 0, 0.15)" : "none",
    transition: theme.transitions.create("all"),
    outline: "none",
    position: "relative",
    cursor: "pointer",
}));

interface Option {
    value: string;
    label: ReactNode;
    description: ReactNode;
}

interface IProps extends RadioGroupProps {
    name: string;
    options: Option[];
    disabled?: boolean;
}

export default function RHFRadioPanelGroup({ name, options, disabled, ...other }: IProps) {
    const { control } = useFormContext();
    const selected = useWatch({ control, name });

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <>
                    <RadioGroup
                        row
                        sx={{
                            width: "100%",
                            display: "grid",
                            gap: 3,
                            gridTemplateColumns: {
                                xs: "1fr",
                                md: `repeat(${options.length}, 1fr)`,
                            },
                        }}
                        {...field}
                        {...other}
                    >
                        {options.map((option) => {
                            const isSelected = selected === option.value;
                            return (
                                <OptionStyle
                                    key={option.value}
                                    selected={isSelected}
                                    sx={{
                                        borderColor: isSelected ? "primary.main" : "transparent",
                                        cursor: disabled ? "not-allowed" : "pointer",
                                    }}
                                    elevation={isSelected ? 2 : 0}
                                    onClick={() => !disabled && field.onChange(option.value)}
                                >
                                    <FormControlLabel
                                        value={option.value}
                                        control={<Radio checked={isSelected} disabled={disabled} />}
                                        label={<>{option.label}</>}
                                        sx={{ m: 0, width: "100%" }}
                                    />
                                    <Box sx={{ color: "text.secondary", mt: 1, ml: 1.5 }}>
                                        {option.description}
                                    </Box>
                                </OptionStyle>
                            );
                        })}
                    </RadioGroup>
                    {!!error && (
                        <FormHelperText error sx={{ px: 2 }}>
                            {error.message}
                        </FormHelperText>
                    )}
                </>
            )}
        />
    );
}
